import { priceOption, CLEANING_SCHEDULES } from '../pricing/price.js'
import { wholeMonthsBetween, splitIntoMonths } from '../pricing/stay.js'
import { quoteUnitOf } from '../quote/quoteUnit.js'

/**
 * The price box on a listing: this home, these dates, and what each cleaning choice costs.
 *
 * IT IS THE QUOTE ENGINE, CALLED THE SAME WAY THE QUOTE BUILDER CALLS IT. The unit comes from
 * `quoteUnitOf`, the arithmetic from `priceOption`. A renter who sees $12,329 here and is then sent
 * a quote for the same home and dates must see $12,329 there too — `tests/listing.test.mjs` holds
 * the two to the cent.
 *
 * `offered` is the cleaning frequencies this home can be listed with (see readiness.js). Each is
 * priced as its own whole option rather than by adding a fee to one total, so what the renter
 * picks is exactly what a quote with that choice would say.
 *
 * `asOf` is the moment of pricing, passed in: a lead-time adjustment that depended on when the
 * page rendered would give a renter who reloads a different price.
 */
export function priceListing({ unit, settings, strategy = null, checkIn, checkOut, cleaning, offered = [], asOf,
  /*
    The deposit policies, for the same reason every other figure here comes from `quoteUnitOf`: a
    renter who sees a deposit on this page and is then sent a quote for the same home and dates
    must see the same one. Leaving them out here would have made the listing and the quote disagree
    about the deposit the day the policy started deciding it. (mtr#115.)
  */
  policies = [] }) {
  const { unit: priced } = quoteUnitOf(unit, settings, { wholeMonths: wholeMonthsBetween(checkIn, checkOut), policies })
  const run = (scheduleId) => priceOption(priced, { checkIn, checkOut, cleaning: scheduleId, strategy, asOf })

  const choices = CLEANING_SCHEDULES.filter((s) => offered.includes(s.id)).map((s) => {
    const option = run(s.id)
    const fee = (option.fees ?? []).find((f) => f.scheduleId === s.id) ?? null
    return {
      id: s.id,
      label: s.label,
      visits: fee?.occurrences ?? null,
      amountCents: fee?.amountCents ?? null,
      option,
    }
  })

  const chosenId = offered.includes(cleaning) ? cleaning : choices[0]?.id ?? null
  const chosen = choices.find((c) => c.id === chosenId) ?? null
  return { chosenId, choices, option: chosen?.option ?? (offered.length ? null : run(cleaning)) }
}

/**
 * The rent a listing card leads with: the stay's rent per month, on average.
 *
 * Months are counted as the calendar counts them — 15 nights of a 30-night month is half a month —
 * so a whole-month stay shows exactly what its months average, and a stay that starts mid-month is
 * not made to look dearer by being divided by the number of months it touches.
 */
export const averageMonthlyCents = (option) => {
  if (!option?.ok) return null
  const { checkIn, checkOut } = option.stay ?? {}
  if (!checkIn || !checkOut) return null
  const months = splitIntoMonths(checkIn, checkOut).reduce((n, s) => n + s.occupiedNights / s.daysInMonth, 0)
  return months > 0 ? Math.round(option.quotedRentCents / months) : null
}

/**
 * ONE VISIT'S SHARE OF A CLEANING CHOICE (the Product Owner, 2026-10-07): the choice's total over its
 * visits, rounded to the cent. A choice of one visit is its total. Shown to the renter first, because
 * a weekly clean is the dearest total and the cheapest visit, and the total alone reads as the dear one.
 */
export const perVisitCents = (c) => {
  if (c?.amountCents == null) return null
  const visits = Number.isInteger(c.visits) && c.visits > 1 ? c.visits : 1
  return Math.round(c.amountCents / visits)
}
